import { computed, type Ref } from 'vue'
import type { PublicStore } from '~/types/PublicStore'
import { StoreStatus } from '~/services/api/interfaces/Store'
import { normalizeStoreStatus } from '~/utils/normalizeStoreStatus'
import type { TranslateFn } from './TranslateFn'
import { resolveClosureMessage } from './resolveClosureMessage'

/**
 * Canonical closure projection: `isTemporarilyClosed`/`activeClosure` reflect only the store
 * resource's own fields; `isFullPageBlockVisible`/`message` also fold in `currentClosedState`.
 */
export function useStoreClosure(
  store: Ref<PublicStore | null>,
  t: TranslateFn,
  defaultMessage: () => string,
  currentClosedState: { readonly value: { message: string } | null } = { value: null }
) {
  const timezone = computed(() => store.value?.timezone ?? 'UTC')
  const isTemporarilyClosed = computed(
    () =>
      store.value?.is_temporarily_closed === true ||
      normalizeStoreStatus(store.value?.status) === StoreStatus.TemporarilyClosed
  )
  const activeClosure = computed(() => store.value?.active_temporary_closure ?? null)
  const isStoreResourceClosed = computed(
    () => isTemporarilyClosed.value || activeClosure.value !== null
  )
  const isFullPageBlockVisible = computed(
    () => isStoreResourceClosed.value || currentClosedState.value !== null
  )
  const message = computed(() => {
    const closureCode = store.value?.closure_code
    if (isStoreResourceClosed.value && closureCode) {
      return resolveClosureMessage(closureCode, timezone.value, t)
    }
    if (currentClosedState.value !== null) return currentClosedState.value.message
    return defaultMessage()
  })

  return { timezone, isTemporarilyClosed, activeClosure, isFullPageBlockVisible, message }
}
